import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  MapPin, 
  Calendar, 
  Users, 
  Zap, 
  Shield, 
  TrendingUp,
  Eye,
  ExternalLink,
  Play,
  CheckCircle,
  Star,
  Globe,
  Award,
  Lightbulb,
  Camera,
  Sun,
  Sparkles,
  BarChart3,
  Battery,
  ArrowLeft,
  Clock,
  DollarSign,
  Target,
  BarChart,
  PieChart,
  Activity,
  Wifi,
  Smartphone,
  Cloud,
  Database,
  Settings,
  Monitor
} from 'lucide-react';
import { mockData } from './mock';

export const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const foundProject = mockData.projects?.find(p => p.id === parseInt(projectId));
    setProject(foundProject);
  }, [projectId]);

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Project Not Found</h1>
          <Button onClick={() => navigate('/projects')}>
            <ArrowLeft size={16} className="mr-2" />
            Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  const getIcon = (iconName) => {
    const icons = {
      Lightbulb, Camera, Eye, Sun, Sparkles, Zap, BarChart3, Battery
    };
    return icons[iconName] || Lightbulb;
  };

  const IconComponent = getIcon(project.icon);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative h-[70vh] overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src={project.image} 
              alt={project.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900/20 via-transparent to-purple-900/20"></div>
          </div>
          
          {/* Floating Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 right-20 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl animate-pulse"></div>
            <div className="absolute bottom-40 left-20 w-40 h-40 bg-purple-500/10 rounded-full blur-2xl animate-pulse delay-1000"></div>
            <div className="absolute top-1/2 right-1/3 w-24 h-24 bg-cyan-500/10 rounded-full blur-xl animate-pulse delay-500"></div>
          </div>

          <div className="absolute inset-0 flex items-end">
            <div className="container mx-auto px-4 pb-12">
              <div className="max-w-5xl">
                <div className="flex items-center gap-4 mb-6">
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/projects')}
                    className="bg-white/20 hover:bg-white/30 text-white border-white/30 backdrop-blur-sm hover:scale-105 transition-all duration-300"
                  >
                    <ArrowLeft size={16} className="mr-2" />
                    Back to Projects
                  </Button>
                  <Badge className="bg-gradient-to-r from-blue-500/90 to-purple-500/90 text-white hover:from-blue-500 hover:to-purple-500 backdrop-blur-sm border-0">
                    {project.category}
                  </Badge>
                </div>
                <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                  <span className="bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent">
                    {project.title}
                  </span>
                </h1>
                <div className="flex flex-wrap items-center gap-8 text-white/90 mb-8">
                  {project.location && (
                    <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                      <MapPin size={20} className="text-blue-300" />
                      <span className="font-medium">{project.location}</span>
                    </div>
                  )}
                  {project.duration && (
                    <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                      <Calendar size={20} className="text-green-300" />
                      <span className="font-medium">{project.duration}</span>
                    </div>
                  )}
                  {project.client && (
                    <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                      <Users size={20} className="text-purple-300" />
                      <span className="font-medium">{project.client}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Project Overview */}
        <section className="py-20 bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 relative overflow-hidden">
          {/* Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-10 left-10 w-72 h-72 bg-gradient-to-r from-blue-200/20 to-purple-200/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-gradient-to-r from-indigo-200/20 to-cyan-200/20 rounded-full blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-3 gap-12 mb-16">
                <div className="lg:col-span-2">
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-indigo-100 px-6 py-3 rounded-full mb-8 border border-blue-200/50">
                    <IconComponent size={20} className="text-blue-600" />
                    <span className="text-blue-700 text-sm font-semibold">Project Overview</span>
                  </div>
                  <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 leading-tight">
                    <span className="bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 bg-clip-text text-transparent">
                      Transforming Vision Into Reality
                    </span>
                  </h2>
                  <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                    {project.description}
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {project.overview}
                  </p>
                </div>
                <div className="space-y-8">
                  <Card className="bg-gradient-to-br from-white to-blue-50/50 border-blue-200/50 hover:shadow-xl transition-all duration-300 hover:scale-105">
                    <CardContent className="pt-8">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                          <IconComponent size={28} className="text-white" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 text-lg">Project Type</h3>
                          <p className="text-sm text-gray-600 capitalize font-medium">{project.category}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-white to-green-50/50 border-green-200/50 hover:shadow-xl transition-all duration-300 hover:scale-105">
                    <CardContent className="pt-8">
                      <h3 className="font-bold text-gray-900 text-lg mb-6">Project Timeline</h3>
                      <div className="space-y-4">
                        {project.duration && (
                          <div className="flex items-center gap-4 bg-gradient-to-r from-blue-50 to-blue-100/50 p-3 rounded-xl">
                            <Clock size={20} className="text-blue-600" />
                            <span className="text-gray-700 font-medium">Duration: {project.duration}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-4 bg-gradient-to-r from-green-50 to-green-100/50 p-3 rounded-xl">
                          <Calendar size={20} className="text-green-600" />
                          <span className="text-gray-700 font-medium">Status: Completed</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {project.client && (
                    <Card className="bg-gradient-to-br from-white to-purple-50/50 border-purple-200/50 hover:shadow-xl transition-all duration-300 hover:scale-105">
                      <CardContent className="pt-8">
                        <h3 className="font-bold text-gray-900 text-lg mb-6">Client</h3>
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 bg-gradient-to-br from-purple-500 via-purple-600 to-indigo-600 rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                            {project.client.charAt(0)}
                          </div>
                          <div>
                            <p className="font-bold text-gray-900 text-lg">{project.client}</p>
                            {project.clientPosition && (
                              <p className="text-sm text-gray-600 font-medium">{project.clientPosition}</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="py-20 bg-gradient-to-br from-gray-50 via-slate-50 to-blue-50/30 relative overflow-hidden">
          {/* Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 right-20 w-80 h-80 bg-gradient-to-r from-green-200/20 to-emerald-200/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-r from-blue-200/20 to-indigo-200/20 rounded-full blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-100 to-emerald-100 px-6 py-3 rounded-full mb-8 border border-green-200/50">
                  <CheckCircle size={20} className="text-green-600" />
                  <span className="text-green-700 text-sm font-semibold">Key Features</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  <span className="bg-gradient-to-r from-gray-900 via-green-800 to-emerald-800 bg-clip-text text-transparent">
                    Advanced Capabilities
                  </span>
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Cutting-edge features that make this project stand out in the industry
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {project.features.map((feature, index) => (
                  <Card key={index} className="bg-gradient-to-br from-white via-green-50/30 to-emerald-50/30 border-green-200/50 hover:shadow-2xl transition-all duration-500 hover:scale-105 group">
                    <CardContent className="pt-8 pb-8">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                          <CheckCircle size={24} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="text-gray-800 text-lg font-medium leading-relaxed">{feature}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Technologies Used */}
        <section className="py-20 bg-gradient-to-br from-white via-indigo-50/30 to-purple-50/30 relative overflow-hidden">
          {/* Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-10 left-10 w-72 h-72 bg-gradient-to-r from-indigo-200/20 to-purple-200/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-gradient-to-r from-blue-200/20 to-cyan-200/20 rounded-full blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-100 to-purple-100 px-6 py-3 rounded-full mb-8 border border-indigo-200/50">
                  <Settings size={20} className="text-indigo-600" />
                  <span className="text-indigo-700 text-sm font-semibold">Technologies Used</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  <span className="bg-gradient-to-r from-gray-900 via-indigo-800 to-purple-800 bg-clip-text text-transparent">
                    Cutting-Edge Technology Stack
                  </span>
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Advanced technologies powering this innovative solution
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {project.technologies.map((tech, index) => {
                  const techIcons = {
                    'IoT Sensors': Wifi,
                    'Cloud Computing': Cloud,
                    'LED Technology': Lightbulb,
                    'Mobile App': Smartphone,
                    'LIDAR Technology': Camera,
                    'AI Processing': Activity,
                    'Real-time Analytics': BarChart,
                    'Mobile Units': Smartphone,
                    'AI Cameras': Camera,
                    'Computer Vision': Eye,
                    'Real-time Processing': Zap,
                    'Control Center Integration': Monitor,
                    'Solar Panels': Sun,
                    'Battery Storage': Battery,
                    'Smart Controllers': Settings,
                    'Weather Monitoring': Sun,
                    'Projection Mapping': Sparkles,
                    'Cultural Event Integration': Star,
                    'Solar Integration': Sun,
                    'Environmental Monitoring': Activity,
                    'Wireless Charging': Zap,
                    'Smart City Connectivity': Wifi,
                    'Modular Design': Settings
                  };
                  
                  const TechIcon = techIcons[tech] || Settings;
                  
                  return (
                    <Card key={index} className="text-center bg-gradient-to-br from-white via-indigo-50/30 to-purple-50/30 border-indigo-200/50 hover:shadow-2xl transition-all duration-500 hover:scale-105 group">
                      <CardContent className="pt-8 pb-8">
                        <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl group-hover:scale-110 transition-transform duration-300">
                          <TechIcon size={36} className="text-white" />
                        </div>
                        <h3 className="font-bold text-gray-900 text-lg">{tech}</h3>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Results & Impact */}
        <section className="py-20 bg-gradient-to-br from-blue-50 via-indigo-50/50 to-purple-50 relative overflow-hidden">
          {/* Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 left-20 w-80 h-80 bg-gradient-to-r from-blue-200/20 to-indigo-200/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-r from-purple-200/20 to-pink-200/20 rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-r from-cyan-200/20 to-blue-200/20 rounded-full blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-indigo-100 px-6 py-3 rounded-full mb-8 border border-blue-200/50">
                  <BarChart size={20} className="text-blue-600" />
                  <span className="text-blue-700 text-sm font-semibold">Results & Impact</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  <span className="bg-gradient-to-r from-gray-900 via-blue-800 to-indigo-800 bg-clip-text text-transparent">
                    Measurable Success
                  </span>
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Outstanding outcomes that demonstrate project excellence and impact
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8 mb-16">
                <Card className="text-center bg-gradient-to-br from-white via-blue-50/50 to-indigo-50/50 border-blue-200/50 hover:shadow-2xl transition-all duration-500 hover:scale-105 group">
                  <CardContent className="pt-10 pb-10">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl group-hover:scale-110 transition-transform duration-300">
                      <Zap size={36} className="text-white" />
                    </div>
                    <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-3">{project.results.energySaved}</div>
                    <p className="text-gray-700 font-semibold text-lg">Energy Savings</p>
                  </CardContent>
                </Card>

                <Card className="text-center bg-gradient-to-br from-white via-green-50/50 to-emerald-50/50 border-green-200/50 hover:shadow-2xl transition-all duration-500 hover:scale-105 group">
                  <CardContent className="pt-10 pb-10">
                    <div className="w-20 h-20 bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl group-hover:scale-110 transition-transform duration-300">
                      <DollarSign size={36} className="text-white" />
                    </div>
                    <div className="text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-3">{project.results.costReduction}</div>
                    <p className="text-gray-700 font-semibold text-lg">Cost Reduction</p>
                  </CardContent>
                </Card>

                <Card className="text-center bg-gradient-to-br from-white via-purple-50/50 to-pink-50/50 border-purple-200/50 hover:shadow-2xl transition-all duration-500 hover:scale-105 group">
                  <CardContent className="pt-10 pb-10">
                    <div className="w-20 h-20 bg-gradient-to-br from-purple-500 via-pink-500 to-rose-500 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl group-hover:scale-110 transition-transform duration-300">
                      <Target size={36} className="text-white" />
                    </div>
                    <div className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-3">{project.results.improvement}</div>
                    <p className="text-gray-700 font-semibold text-lg">Efficiency Improvement</p>
                  </CardContent>
                </Card>
              </div>

              {/* Additional Metrics */}
              <div className="grid md:grid-cols-2 gap-8">
                <Card className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/30 border-blue-200/50 hover:shadow-xl transition-all duration-300">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3 text-xl">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                        <BarChart size={20} className="text-white" />
                      </div>
                      Performance Metrics
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div className="flex justify-between items-center p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
                        <span className="text-gray-700 font-medium">System Uptime</span>
                        <span className="font-bold text-blue-600 text-lg">99.8%</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl">
                        <span className="text-gray-700 font-medium">Response Time</span>
                        <span className="font-bold text-green-600 text-lg">&lt; 100ms</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
                        <span className="text-gray-700 font-medium">Data Accuracy</span>
                        <span className="font-bold text-purple-600 text-lg">99.9%</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-gradient-to-r from-orange-50 to-red-50 rounded-xl">
                        <span className="text-gray-700 font-medium">Maintenance Cost</span>
                        <span className="font-bold text-orange-600 text-lg">-60%</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-white via-green-50/30 to-emerald-50/30 border-green-200/50 hover:shadow-xl transition-all duration-300">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3 text-xl">
                      <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                        <PieChart size={20} className="text-white" />
                      </div>
                      Environmental Impact
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div className="flex justify-between items-center p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl">
                        <span className="text-gray-700 font-medium">CO₂ Reduction</span>
                        <span className="font-bold text-green-600 text-lg">2.5 tons/year</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl">
                        <span className="text-gray-700 font-medium">Energy Efficiency</span>
                        <span className="font-bold text-blue-600 text-lg">A+ Rating</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl">
                        <span className="text-gray-700 font-medium">Renewable Energy</span>
                        <span className="font-bold text-purple-600 text-lg">85%</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl">
                        <span className="text-gray-700 font-medium">Waste Reduction</span>
                        <span className="font-bold text-orange-600 text-lg">40%</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Client Testimonial */}
        <section className="py-20 bg-gradient-to-br from-white via-yellow-50/30 to-orange-50/30 relative overflow-hidden">
          {/* Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 right-20 w-80 h-80 bg-gradient-to-r from-yellow-200/20 to-orange-200/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-r from-amber-200/20 to-yellow-200/20 rounded-full blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-100 to-orange-100 px-6 py-3 rounded-full mb-8 border border-yellow-200/50">
                  <Star size={20} className="text-yellow-600" />
                  <span className="text-yellow-700 text-sm font-semibold">Client Testimonial</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  <span className="bg-gradient-to-r from-gray-900 via-yellow-800 to-orange-800 bg-clip-text text-transparent">
                    What Our Client Says
                  </span>
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Real feedback from satisfied clients about this project
                </p>
              </div>

              <Card className="max-w-4xl mx-auto bg-gradient-to-br from-white via-yellow-50/30 to-orange-50/30 border-yellow-200/50 hover:shadow-2xl transition-all duration-500">
                <CardContent className="pt-12 pb-12">
                  <div className="flex items-center gap-2 mb-8 justify-center">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={24} className="text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <blockquote className="text-2xl text-gray-800 italic text-center mb-10 leading-relaxed font-medium">
                    "{project.testimonial}"
                  </blockquote>
                  <div className="flex items-center justify-center gap-6">
                    {project.client && (
                      <div className="w-20 h-20 bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 rounded-3xl flex items-center justify-center text-white font-bold text-2xl shadow-xl">
                        {project.client.charAt(0)}
                      </div>
                    )}
                    <div className="text-center">
                      {project.client && (
                        <p className="font-bold text-gray-900 text-xl">{project.client}</p>
                      )}
                      {project.clientPosition && (
                        <p className="text-gray-600 text-lg">{project.clientPosition}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Project Gallery */}
        <section className="py-20 bg-gradient-to-br from-gray-50 via-slate-50 to-blue-50/30 relative overflow-hidden">
          {/* Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 left-20 w-80 h-80 bg-gradient-to-r from-slate-200/20 to-gray-200/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-r from-blue-200/20 to-indigo-200/20 rounded-full blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-slate-100 to-gray-100 px-6 py-3 rounded-full mb-8 border border-slate-200/50">
                  <Camera size={20} className="text-slate-600" />
                  <span className="text-slate-700 text-sm font-semibold">Project Gallery</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  <span className="bg-gradient-to-r from-gray-900 via-slate-800 to-blue-800 bg-clip-text text-transparent">
                    Visual Documentation
                  </span>
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Comprehensive visual documentation of the project implementation
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <Card className="overflow-hidden bg-gradient-to-br from-white via-slate-50/30 to-gray-50/30 border-slate-200/50 hover:shadow-2xl transition-all duration-500 hover:scale-105 group">
                  <div className="relative overflow-hidden">
                    <img 
                      src={project.image} 
                      alt={`${project.title} - Main View`}
                      className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  </div>
                  <CardContent className="pt-6 pb-6">
                    <h3 className="font-bold text-gray-900 text-xl mb-2">Main Implementation</h3>
                    <p className="text-gray-600 text-lg">Primary installation showcasing the core technology and design</p>
                  </CardContent>
                </Card>

                <Card className="overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/30 border-blue-200/50 hover:shadow-2xl transition-all duration-500 hover:scale-105 group">
                  <div className="relative overflow-hidden">
                    <img 
                      src={project.image} 
                      alt={`${project.title} - Detail View`}
                      className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  </div>
                  <CardContent className="pt-6 pb-6">
                    <h3 className="font-bold text-gray-900 text-xl mb-2">Technical Details</h3>
                    <p className="text-gray-600 text-lg">Close-up view of the technology components and integration</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 text-white relative overflow-hidden">
          {/* Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 right-20 w-80 h-80 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-r from-indigo-500/10 to-blue-500/10 rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-4 text-center relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 px-6 py-3 rounded-full mb-8 border border-blue-400/30 backdrop-blur-sm">
                <ExternalLink size={20} className="text-blue-300" />
                <span className="text-blue-200 text-sm font-semibold">Get Started</span>
              </div>
              <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                <span className="bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent">
                  Ready to Transform Your City?
                </span>
              </h2>
              <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
                Let's discuss how we can implement similar innovative technology solutions for your city or organization. Our team is ready to help you achieve your smart city goals.
              </p>
              <div className="flex justify-center">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-12 py-4 text-lg font-semibold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                  onClick={() => navigate('/contact')}
                >
                  <ExternalLink size={24} className="mr-3" />
                  Start Your Project
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};
